import React from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Chip, Stack, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, ThumbUp, Comment, Edit, RecordVoiceOver } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { LevelChip } from '../Common';

const VideoPostCard = ({ post, isShort = false, showTags = true }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        // Navigate to VideoPostDetail page with the post id
        if (post?._id) {
            navigate(`/video-posts/${post._id}`);
        }
    };



    if (!post) {
        return null;
    }

    return (
        <Card 
            elevation={0}
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, opacity 0.2s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    opacity: 0.9,
                }
            }}
            onClick={handleClick}
        >
            <Box sx={{ height: isShort ? '400px' : '280px', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <CardMedia
                    component="img"
                    image={post.thumbUrl || '/placeholder-image.jpg'}
                    alt={post.title}
                    sx={{ 
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain'
                    }}
                />
            </Box>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    gutterBottom 
                    noWrap 
                    sx={{ fontSize: '1rem' }}
                >
                    {post.title}
                </Typography>

                {/* Learner Level */}
                {post.level && (
                    <Box sx={{ mb: 1.5 }}>
                        <LevelChip level={post.level} variant="filled" size="small" />
                    </Box>
                )}

                {/* Stats Row - Words and Phrases */}
                <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                    {post.words && (
                        <Tooltip title="Total words">
                            <Chip
                                label={`${post.words} words`}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: '20px' }}
                            />
                        </Tooltip>
                    )}
                    {post.phrases && (
                        <Tooltip title="Phrases">
                            <Chip
                                label={`${post.phrases} phrases`}
                                size="small"
                                color="secondary"
                                sx={{ fontSize: '0.65rem', height: '20px' }}
                            />
                        </Tooltip>
                    )}
                    {/* Mock data if not available */}
                    {!post.words && (
                        <Tooltip title="Total words">
                            <Chip
                                label={`${Math.floor(Math.random() * 1000) + 200} words`}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: '20px' }}
                            />
                        </Tooltip>
                    )}
                    {!post.phrases && (
                        <Tooltip title="Phrases">
                            <Chip
                                label={`${Math.floor(Math.random() * 50) + 10} phrases`}
                                size="small"
                                color="secondary"
                                sx={{ fontSize: '0.65rem', height: '20px' }}
                            />
                        </Tooltip>
                    )}
                </Stack>

                {/* Tags */}
                {showTags && post.tags && post.tags.length > 0 && (
                    <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {post.tags.slice(0, 3).map((tag, idx) => (
                            <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: '18px' }}
                            />
                        ))}
                        {post.tags.length > 3 && (
                            <Chip
                                label={`+${post.tags.length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: '18px' }}
                            />
                        )}
                    </Box>
                )}

                {/* Engagement Stats */}
                <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Tooltip title={`${post.views || Math.floor(Math.random() * 5000)} views`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Visibility sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {post.views || Math.floor(Math.random() * 5000)}
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip title={`${post.likes || Math.floor(Math.random() * 500)} likes`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ThumbUp sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {post.likes || Math.floor(Math.random() * 500)}
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip title={`${post.comments || Math.floor(Math.random() * 50)} discussions`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Comment sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {post.comments || Math.floor(Math.random() * 50)}
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip title={`${Math.floor(Math.random() * 20) + 5} practices`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Edit sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                                <RecordVoiceOver sx={{ fontSize: '0.9rem', color: 'secondary.main' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {Math.floor(Math.random() * 20) + 5}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

VideoPostCard.propTypes = {
    post: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        thumbUrl: PropTypes.string,
        author: PropTypes.string,
        estimatedWatchTime: PropTypes.number,
        language: PropTypes.shape({
            name: PropTypes.string
        }),
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        isReadyToServe: PropTypes.bool,
        transcripts: PropTypes.array,
        tags: PropTypes.arrayOf(PropTypes.string)
    }),
    isShort: PropTypes.bool,
    showTags: PropTypes.bool
};

export default VideoPostCard; 